import { useState, useRef, useEffect, useCallback } from 'react';
import { Modal, Input, message, Spin } from 'antd';
import { RotateLeftOutlined, RotateRightOutlined } from '@ant-design/icons';
import { Button } from '@bancobarigui/barihaus';
import { docmanService } from '@/services/docman.service';

const { TextArea } = Input;

export interface ImageEditorModalProps {
  open: boolean;
  onCancel: () => void;
  onSave: (result: ImageEditorResult) => void;
  /** documentId for S3 images (used to load the image) */
  documentId?: string;
  /** Direct base64/URL source (for facade photo) */
  imageSrc?: string;
  /** Current caption/description */
  caption?: string;
  /** Whether saving is in progress (controlled externally) */
  saving?: boolean;
  /** Title override */
  title?: string;
}

export interface ImageEditorResult {
  /** The rotated image as a File (null if no rotation happened) */
  rotatedFile: File | null;
  /** The updated caption */
  caption: string;
  /** Whether the image was rotated */
  wasRotated: boolean;
  /** Rotated image as base64 (for facade photo use case) */
  rotatedBase64: string | null;
}

export function ImageEditorModal({
  open,
  onCancel,
  onSave,
  documentId,
  imageSrc,
  caption: initialCaption = '',
  saving = false,
  title = 'Editar Imagem',
}: ImageEditorModalProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [caption, setCaption] = useState(initialCaption);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  // Load image when modal opens
  useEffect(() => {
    if (!open) {
      setRotation(0);
      setCaption(initialCaption);
      setImageUrl(null);
      imageRef.current = null;
      return;
    }

    setCaption(initialCaption);

    if (imageSrc) {
      setImageUrl(imageSrc);
      loadImageElement(imageSrc);
    } else if (documentId) {
      setLoading(true);
      const docId = documentId.startsWith('docman/')
        ? documentId.replace('docman/', '')
        : documentId;
      docmanService
        .getImageUrl(docId)
        .then((url) => {
          setImageUrl(url);
          loadImageElement(url);
        })
        .catch(() => {
          message.error('Erro ao carregar imagem');
        })
        .finally(() => setLoading(false));
    }
  }, [open, documentId, imageSrc, initialCaption]);

  const loadImageElement = (src: string) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      drawCanvas(img, 0);
    };
    img.onerror = () => {
      // Try fetching as blob to bypass CORS
      fetch(src)
        .then((r) => r.blob())
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const img2 = new Image();
          img2.onload = () => {
            imageRef.current = img2;
            drawCanvas(img2, 0);
          };
          img2.src = url;
        })
        .catch(() => message.error('Erro ao carregar imagem para edição'));
    };
    img.src = src;
  };

  const drawCanvas = useCallback(
    (img: HTMLImageElement, deg: number) => {
      const canvas = canvasRef.current;
      if (!canvas || !img) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const isRotated90 = deg % 180 !== 0;
      const w = isRotated90 ? img.naturalHeight : img.naturalWidth;
      const h = isRotated90 ? img.naturalWidth : img.naturalHeight;

      // Scale to fit modal (max 500px)
      const maxSize = 500;
      const scale = Math.min(maxSize / w, maxSize / h, 1);
      const displayW = Math.round(w * scale);
      const displayH = Math.round(h * scale);

      canvas.width = displayW;
      canvas.height = displayH;

      ctx.clearRect(0, 0, displayW, displayH);
      ctx.save();
      ctx.translate(displayW / 2, displayH / 2);
      ctx.rotate((deg * Math.PI) / 180);

      const imgDisplayW = isRotated90 ? displayH : displayW;
      const imgDisplayH = isRotated90 ? displayW : displayH;
      ctx.drawImage(img, -imgDisplayW / 2, -imgDisplayH / 2, imgDisplayW, imgDisplayH);
      ctx.restore();
    },
    []
  );

  const handleRotate = (direction: 'left' | 'right') => {
    const newRotation = direction === 'right' ? rotation + 90 : rotation - 90;
    const normalized = ((newRotation % 360) + 360) % 360;
    setRotation(normalized);

    if (imageRef.current) {
      drawCanvas(imageRef.current, normalized);
    }
  };

  const getRotatedFile = (): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = imageRef.current;
      if (!img) {
        reject(new Error('Imagem não carregada'));
        return;
      }

      // Use full resolution for the output file
      const exportCanvas = document.createElement('canvas');
      const ctx = exportCanvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Erro ao criar canvas'));
        return;
      }

      const isRotated90 = rotation % 180 !== 0;
      const w = isRotated90 ? img.naturalHeight : img.naturalWidth;
      const h = isRotated90 ? img.naturalWidth : img.naturalHeight;

      exportCanvas.width = w;
      exportCanvas.height = h;

      ctx.translate(w / 2, h / 2);
      ctx.rotate((rotation * Math.PI) / 180);

      const imgW = isRotated90 ? h : w;
      const imgH = isRotated90 ? w : h;
      ctx.drawImage(img, -imgW / 2, -imgH / 2, imgW, imgH);

      exportCanvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Erro ao gerar imagem'));
            return;
          }
          const file = new File([blob], `rotated_${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });
          resolve(file);
        },
        'image/jpeg',
        0.92
      );
    });
  };

  const getRotatedBase64 = (): string | null => {
    const img = imageRef.current;
    if (!img) return null;

    const exportCanvas = document.createElement('canvas');
    const ctx = exportCanvas.getContext('2d');
    if (!ctx) return null;

    const isRotated90 = rotation % 180 !== 0;
    const w = isRotated90 ? img.naturalHeight : img.naturalWidth;
    const h = isRotated90 ? img.naturalWidth : img.naturalHeight;

    exportCanvas.width = w;
    exportCanvas.height = h;

    ctx.translate(w / 2, h / 2);
    ctx.rotate((rotation * Math.PI) / 180);

    const imgW = isRotated90 ? h : w;
    const imgH = isRotated90 ? w : h;
    ctx.drawImage(img, -imgW / 2, -imgH / 2, imgW, imgH);

    return exportCanvas.toDataURL('image/jpeg', 0.92);
  };

  const handleSave = async () => {
    const wasRotated = rotation !== 0;
    let rotatedFile: File | null = null;
    let rotatedBase64: string | null = null;

    if (wasRotated) {
      try {
        rotatedFile = await getRotatedFile();
        rotatedBase64 = getRotatedBase64();
      } catch {
        message.error('Erro ao processar imagem rotacionada');
        return;
      }
    }

    onSave({
      rotatedFile,
      caption,
      wasRotated,
      rotatedBase64,
    });
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancelar
        </Button>,
        <Button key="save" buttonColor="primary" onClick={handleSave} loading={saving}>
          Salvar
        </Button>,
      ]}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: '#8c8c8c' }}>Carregando imagem...</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Image preview with rotation */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 300,
              backgroundColor: '#f5f5f5',
              borderRadius: 8,
              padding: 16,
            }}
          >
            <canvas
              ref={canvasRef}
              style={{
                maxWidth: '100%',
                borderRadius: 4,
              }}
            />
          </div>

          {/* Rotation controls */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
            <Button
              icon={<RotateLeftOutlined />}
              onClick={() => handleRotate('left')}
            >
              Girar Esquerda
            </Button>
            <Button
              icon={<RotateRightOutlined />}
              onClick={() => handleRotate('right')}
            >
              Girar Direita
            </Button>
          </div>

          {/* Caption input */}
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
              Legenda
            </label>
            <TextArea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Adicione uma legenda para esta imagem"
              rows={2}
              maxLength={500}
              showCount
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
